/**
 * Veraciter Central, the right-hand culture panel on Vera Home.
 *
 * Its header ("Veraciter Central") lives in HomePage's sky band, which spans
 * both columns; `central-title` is the id this aside is labelled by.
 *
 * Top to bottom: Ideas, This or That, the latest shout out award winner (a
 * small tile, Sasha 10/20: the Spotlight is for something special, not a
 * weekly fixture), the "Give a shout out" button, your own
 * shout outs still waiting (or turned down), the five latest approved shout
 * outs, the link to all of them, and the admin chips. The Spotlight is NOT in
 * here: it has its own place on the page (HomePage) so this column stays as
 * designed (Sasha 10/02). People (HRIS) is
 * Phase 4; the panel does not advertise it (Sasha 9/27). One summary call feeds it all; if Central is down or
 * the caller is outside the preview, the panel falls back to Ideas alone and
 * the home page is unaffected.
 *
 * Plan: docs/design/home/PLAN.md.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ArrowRightRegular, LightbulbRegular, MegaphoneLoudFilled, TrophyFilled } from '@fluentui/react-icons';
import { borderRadius, semanticColors, veracityColors } from '@shared/ui';
import { useCentralSummary, useReview, type HomeWinner, type ShoutOut } from './central/api';
import { ViewAsUserButton } from './central/ViewAsUser';
import type { LauncherStats } from './useLauncherStats';
import { ShoutOutCard } from './central/ShoutOutCard';
import { ShoutOutComposer } from './central/ShoutOutComposer';
import { ThisOrThat } from './central/ThisOrThat';
import { Initials, recipientsLabel, relativeTime } from './central/bits';
import { useAuth } from '@shared/providers/AuthProvider';

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: veracityColors.firstLight,
    borderLeft: `1px solid ${semanticColors.borderSubtle}`,
    minWidth: 0,
    '@media (max-width: 1024px)': {
      borderLeft: 'none',
      borderTop: `1px solid ${semanticColors.borderSubtle}`,
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '18px 24px 28px',
  },

  // Slim row: a pill holding an icon disc, a two-line label and an action.
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 6px 6px 8px',
    borderRadius: borderRadius.circular,
    backgroundColor: '#FFFFFF',
    border: `1px solid ${semanticColors.borderSubtle}`,
  },
  rowDisc: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    fontSize: '18px',
    backgroundColor: veracityColors.firstLight,
    color: veracityColors.veracityOrange,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
    color: veracityColors.midnightCharcoal,
    ':hover': { textDecoration: 'underline' },
  },
  rowLabel: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: tokens.colorBrandForegroundLink,
  },
  rowValue: {
    fontSize: '12.5px',
    fontWeight: 600,
  },
  rowAction: {
    flexShrink: 0,
    padding: '7px 12px',
    borderRadius: borderRadius.circular,
    backgroundColor: veracityColors.firstLight,
    color: tokens.colorBrandForegroundLink,
    fontSize: '12px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    ':hover': { backgroundColor: veracityColors.gentleHaze },
  },

  // The award winner: a small tile under the header, warm but quieter than
  // the button below it.
  winner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px 8px 8px',
    borderRadius: borderRadius.large,
    backgroundColor: '#FFFFFF',
    border: `1.5px solid ${veracityColors.daybreakAmber}`,
    textDecoration: 'none',
    color: veracityColors.midnightCharcoal,
    ':hover': { backgroundColor: veracityColors.firstLight },
    ':focus-visible': { outline: `2px solid ${veracityColors.midnightCharcoal}`, outlineOffset: '2px' },
  },
  winnerText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' },
  winnerName: { fontSize: '14px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  winnerWhy: {
    fontSize: '12px',
    color: semanticColors.textSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  winnerPopover: { maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', lineHeight: 1.5 },
  winnerPopTitle: { fontWeight: 700, color: veracityColors.midnightCharcoal },
  winnerPopMsg: { color: '#3A3431', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  winnerPopFrom: { fontSize: '12px', color: semanticColors.textSecondary },

  // The primary call to action.
  // Dusk Blue with a megaphone (Sasha 9/27), so the one big button in the
  // panel does not compete with the orange sky above it. Hover brightens it
  // rather than switching to an off-palette shade.
  give: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '13px 22px',
    border: 'none',
    borderRadius: borderRadius.circular,
    backgroundColor: veracityColors.duskBlue,
    color: '#FFFFFF',
    fontFamily: 'inherit',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transitionProperty: 'transform, background-color',
    transitionDuration: '160ms',
    ':hover': { filter: 'brightness(1.15)', transform: 'translateY(-2px)' },
    ':focus-visible': { outline: `2px solid ${veracityColors.midnightCharcoal}`, outlineOffset: '2px' },
    '@media (prefers-reduced-motion: reduce)': { ':hover': { transform: 'none' } },
  },

  // Shout Out Admins approve right here (Sasha 9/27).
  review: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    borderRadius: borderRadius.editorial,
    backgroundColor: '#FFFFFF',
    border: `1.5px solid ${veracityColors.daybreakAmber}`,
  },
  reviewHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: veracityColors.midnightCharcoal,
  },
  reviewItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingTop: '8px',
    borderTop: `1px solid ${semanticColors.borderSubtle}`,
    fontSize: '12.5px',
    lineHeight: 1.45,
  },
  reviewWho: { display: 'flex', alignItems: 'center', gap: '8px', color: semanticColors.textSecondary },
  reviewMsg: { color: '#3A3431', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  reviewActions: { display: 'flex', gap: '6px', alignItems: 'center' },
  reviewMore: { fontSize: '12px', color: tokens.colorBrandForegroundLink },

  mine: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 12px',
    borderRadius: borderRadius.large,
    backgroundColor: '#FFFFFF',
    border: `1px dashed ${semanticColors.borderDefault}`,
    fontSize: '12.5px',
    color: semanticColors.textSecondary,
    lineHeight: 1.45,
  },
  mineStrong: { fontWeight: 600, color: veracityColors.midnightCharcoal },

  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '11.5px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: tokens.colorBrandForegroundLink,
    '::after': {
      content: '""',
      flex: 1,
      height: '1px',
      backgroundColor: semanticColors.borderDefault,
    },
  },
  // The Academy certificate's balloon dog, shrunk to sit beside the header
  // text (Sasha 9/28: "a little balloon dog next to the header text").
  bark: {
    height: '26px',
    width: 'auto',
    flexShrink: 0,
    filter: 'drop-shadow(1px 1px 1.5px rgba(38, 20, 28, 0.3))',
  },
  cards: { display: 'flex', flexDirection: 'column', gap: '10px' },
  empty: { fontSize: '13px', color: semanticColors.textSecondary },

  seeAll: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '9px 18px',
    borderRadius: borderRadius.circular,
    border: `1.5px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorBrandForegroundLink,
    fontSize: '13.5px',
    fontWeight: 700,
    textDecoration: 'none',
    ':hover': { backgroundColor: tokens.colorBrandBackground, color: '#FFFFFF' },
  },

  chips: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip: {
    padding: '5px 11px',
    borderRadius: borderRadius.circular,
    fontSize: '12px',
    fontWeight: 600,
    textDecoration: 'none',
  },
  chipAction: {
    backgroundColor: veracityColors.gentleHaze,
    color: veracityColors.midnightCharcoal,
    ':hover': { textDecoration: 'underline' },
  },
  chipQuiet: {
    backgroundColor: '#F4F1EC',
    color: semanticColors.textSecondary,
  },
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortDay = (ymd: string) => {
  const [, m, d] = ymd.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
};

/** The latest locked shout out award: who, which stretch, and for what. */
function WinnerTile({ w }: { w: HomeWinner }) {
  const styles = useStyles();
  const when = w.windowStart === w.windowEnd ? shortDay(w.windowStart) : `${shortDay(w.windowStart)} to ${shortDay(w.windowEnd)}`;
  const title = w.shoutOutTitle ? `"${w.shoutOutTitle}"` : null;
  const from = w.shoutOutSenderName ? `from ${w.shoutOutSenderName}` : null;
  const why =
    w.pool === 'given'
      ? title
        ? `For giving ${title}`
        : 'For giving a shout out'
      : title
        ? `For ${title}${from ? `, ${from}` : ''}`
        : from && `Shout out ${from}`;
  const tile = (
    <Link
      to={w.shoutOutId ? `/central/shout-outs?id=${w.shoutOutId}` : '/central/shout-outs'}
      className={styles.winner}
      aria-label={`Shout out award winner, ${when}: ${w.winner.name}`}
    >
      <span className={styles.rowDisc} aria-hidden>
        <TrophyFilled />
      </span>
      <span className={styles.winnerText}>
        <span className={styles.rowLabel}>Shout out winner · {when}</span>
        <span className={styles.winnerName}>{w.winner.name}</span>
        {why && <span className={styles.winnerWhy}>{why}</span>}
      </span>
      <Initials name={w.winner.name} size={30} />
    </Link>
  );
  if (!w.shoutOutMessage) return tile;
  // The whole shout out on hover, same as the cards below (Sasha 10/20).
  return (
    <Popover openOnHover mouseLeaveDelay={150} withArrow positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>{tile}</PopoverTrigger>
      <PopoverSurface className={styles.winnerPopover}>
        {w.shoutOutTitle && <div className={styles.winnerPopTitle}>{w.shoutOutTitle}</div>}
        <div className={styles.winnerPopMsg}>{w.shoutOutMessage}</div>
        {w.shoutOutSenderName && <div className={styles.winnerPopFrom}>from {w.shoutOutSenderName}</div>}
      </PopoverSurface>
    </Popover>
  );
}

export interface CentralPanelProps {
  /** The user can open Ideas (open to everyone today, but not assumed). */
  showIdeas: boolean;
  ideas: LauncherStats['ideas'] | undefined;
}

/** One pending shout out with Approve / Reject (and an optional note on reject). */
function ReviewItem({ s }: { s: ShoutOut }) {
  const styles = useStyles();
  const review = useReview();
  const { user } = useAuth();
  const own = user?.oid === s.sender.oid;
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  return (
    <div className={styles.reviewItem}>
      <span className={styles.reviewWho}>
        <Initials name={s.recipients[0]?.name ?? '?'} size={22} />
        <span>
          <strong style={{ color: veracityColors.midnightCharcoal }}>{recipientsLabel(s.recipients)}</strong> · from{' '}
          {s.sender.name} · {relativeTime(s.createdAt)} · {s.valueLabel}
        </span>
      </span>
      <span className={styles.reviewMsg}>{s.message}</span>
      {own ? (
        <span className={styles.reviewWho}>Your own shout out: another Shout Out Admin reviews it.</span>
      ) : rejecting ? (
        <div className={styles.reviewActions}>
          <Input
            size="small"
            style={{ flex: 1, minWidth: 0 }}
            placeholder="Note to the sender (optional)"
            value={note}
            maxLength={500}
            onChange={(_, d) => setNote(d.value)}
          />
          <Button size="small" onClick={() => setRejecting(false)}>
            Back
          </Button>
          <Button
            size="small"
            appearance="primary"
            disabled={review.isPending}
            onClick={() => review.mutate({ id: s.id, decision: 'reject', note })}
          >
            Reject
          </Button>
        </div>
      ) : (
        <div className={styles.reviewActions}>
          <Button
            size="small"
            appearance="primary"
            disabled={review.isPending}
            onClick={() => review.mutate({ id: s.id, decision: 'approve', note: '' })}
          >
            Approve
          </Button>
          <Button size="small" disabled={review.isPending} onClick={() => setRejecting(true)}>
            Reject…
          </Button>
        </div>
      )}
      {review.error && <span style={{ color: tokens.colorPaletteRedForeground1 }}>{review.error.message}</span>}
    </div>
  );
}

export function CentralPanel({ showIdeas, ideas }: CentralPanelProps) {
  const styles = useStyles();
  const summary = useCentralSummary();
  const [openThread, setOpenThread] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  // Open and shipped-this-year, not "new": 'new' means untriaged, so a board
  // someone looks after read as 0 (Sasha 9/27).
  const ideasLine =
    typeof ideas?.openIdeas === 'number'
      ? `${ideas.openIdeas.toLocaleString()} open · ${ideas.shippedThisYear ?? 0} shipped in ${ideas.year}`
      : 'What would make work better?';

  const c = summary.data;
  const live = c?.enabled === true;
  const pending = c?.mine.filter((s) => s.status === 'pending') ?? [];
  const rejected = c?.mine.filter((s) => s.status === 'rejected') ?? [];

  return (
    <aside className={styles.panel} aria-labelledby="central-title">
      <div className={styles.body}>
        {showIdeas && (
          <div className={styles.row}>
            <span className={styles.rowDisc} aria-hidden>
              <LightbulbRegular />
            </span>
            <Link to="/ideas" className={styles.rowText}>
              <span className={styles.rowLabel}>Ideas</span>
              <span className={styles.rowValue}>{ideasLine}</span>
            </Link>
            <Link to="/ideas" className={styles.rowAction}>
              + Post
            </Link>
          </div>
        )}

        {live && c.poll && <ThisOrThat poll={c.poll} />}

        {live && c.isShoutOutAdmin && c.pending.length > 0 && (
          <section className={styles.review} aria-label="Shout outs awaiting approval">
            <span className={styles.reviewHead}>
              Awaiting your approval
              <span>{c.pendingCount}</span>
            </span>
            {c.pending.slice(0, 3).map((s) => (
              <ReviewItem key={s.id} s={s} />
            ))}
            {(c.pendingCount ?? 0) > 3 && (
              <Link to="/central/admin" className={styles.reviewMore}>
                {(c.pendingCount ?? 0) - 3} more in the queue
              </Link>
            )}
          </section>
        )}

        {live && (
          <>
            <span className={styles.sectionLabel}>
              <img className={styles.bark} src="/balloon-dog.webp" alt="" aria-hidden="true" />
              The Victory Bark
            </span>
            {c.winner && <WinnerTile w={c.winner} />}
            <button type="button" className={styles.give} onClick={() => setComposing(true)}>
              <MegaphoneLoudFilled style={{ fontSize: 20 }} aria-hidden />
              Give a shout out
            </button>

            {(pending.length > 0 || rejected.length > 0) && (
              <div className={styles.mine}>
                {pending.length > 0 && (
                  <span>
                    <span className={styles.mineStrong}>
                      {pending.length === 1
                        ? `Your shout out to ${recipientsLabel(pending[0].recipients)}`
                        : `${pending.length} of your shout outs`}
                    </span>{' '}
                    {pending.length === 1 ? 'is' : 'are'} waiting for approval.
                  </span>
                )}
                {rejected.map((s) => (
                  <span key={s.id}>
                    <span className={styles.mineStrong}>
                      Your shout out to {recipientsLabel(s.recipients)} was not approved
                    </span>
                    {s.reviewNote ? `: "${s.reviewNote}"` : '.'}
                  </span>
                ))}
              </div>
            )}

            <div className={styles.cards}>
              {c.shoutOuts.length === 0 ? (
                <span className={styles.empty}>No shout outs yet. Be the first to give one.</span>
              ) : (
                c.shoutOuts.map((s) => (
                  <ShoutOutCard
                    key={s.id}
                    shoutOut={s}
                    open={openThread === s.id}
                    onToggle={() => setOpenThread((cur) => (cur === s.id ? null : s.id))}
                    canModerate={c.isShoutOutAdmin}
                  />
                ))
              )}
            </div>

            {c.shoutOuts.length > 0 && (
              <Link to="/central/shout-outs" className={styles.seeAll}>
                {c.shoutOutYearTotal} shout out{c.shoutOutYearTotal === 1 ? '' : 's'} in {c.year}: see all <ArrowRightRegular />
              </Link>
            )}

            <div className={styles.chips}>
              {c.isShoutOutAdmin ? (
                <Link to="/central/admin" className={mergeClasses(styles.chip, styles.chipAction)}>
                  {c.pendingCount ? 'Shout out admin' : 'Queue is clear · shout out admin'}
                </Link>
              ) : (
                <span className={mergeClasses(styles.chip, styles.chipQuiet)}>
                  Moderated by Shout Out Admins
                </span>
              )}
              {c.isPollAdmin && (
                <Link to="/central/this-or-that" className={mergeClasses(styles.chip, styles.chipAction)}>
                  {c.poll ? 'Manage This or That' : 'No poll this week: manage This or That'}
                </Link>
              )}
              {c.isSpotlightAdmin && (
                <Link to="/central/spotlight" className={mergeClasses(styles.chip, styles.chipAction)}>
                  {c.spotlights.length > 0 ? 'Manage Spotlight' : 'Post a Spotlight'}
                </Link>
              )}
              {(c.isShoutOutAdmin || c.isPollAdmin || c.isSpotlightAdmin) && (
                <Link to="/central/features" className={mergeClasses(styles.chip, styles.chipAction)}>
                  All Central features
                </Link>
              )}
              <ViewAsUserButton />
            </div>
          </>
        )}
      </div>

      {live && (
        <ShoutOutComposer open={composing} onClose={() => setComposing(false)} values={c.values} />
      )}
    </aside>
  );
}
